import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../utils/authContext';
import { useToast } from '../../components/Auth/Toast';
import { ToastContainer } from '../../components/Auth/Toast';
import Spinner from '../../components/Auth/Spinner';
import './AuthPages.css';

const SignupPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const { signup, loading, error, setError } = useAuth();
  const { toasts, addToast, removeToast } = useToast();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    if (!formData.name) {
      addToast('Please enter your name', 'error');
      return false;
    }
    if (formData.name.length < 3) {
      addToast('Name must be at least 3 characters', 'error');
      return false;
    }
    if (!formData.email) {
      addToast('Please enter your email', 'error');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      addToast('Please enter a valid email', 'error');
      return false;
    }
    if (!formData.password) {
      addToast('Please enter a password', 'error');
      return false;
    }
    if (formData.password.length < 6) {
      addToast('Password must be at least 6 characters', 'error');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      addToast('Passwords do not match', 'error');
      return false;
    }
    if (!agreedToTerms) {
      addToast('Please agree to the terms and conditions', 'error');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    const payload = {
      ...formData,
      name: formData.name.trim(),
      email: formData.email.trim().toLowerCase()
    };

    try {
      await signup(payload);
      addToast('Account created successfully! Logging in...', 'success');
      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (err) {
      addToast(err.message || error || 'Signup failed', 'error');
    }
  };

  return (
    <div className='auth-container'>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      
      <div className='auth-page-wrapper'>
        {/* Logo Section */}
        <div className='auth-logo-section'>
          <div className='auth-logo-container'>
            <img
              src={require('../../assets/logo/logo1.jpeg')}
              alt='Laat Sahab News Logo'
              className='auth-logo-img'
            />
            <div className='auth-logo-text'>Laat Sahab News</div>
          </div>
        </div>

        {/* Form Section */}
        <div className='auth-form-wrapper'>
          <div className='auth-header'>
            <h1>📝 Create Account</h1>
            <p>Join Laat Sahab News Community</p>
          </div>

          <form onSubmit={handleSubmit} className='auth-form'>
            <div className='form-group'>
              <label htmlFor='name'>Full Name</label>
              <input
                type='text'
                id='name'
                name='name'
                value={formData.name}
                onChange={handleChange}
                placeholder='Enter your full name'
                disabled={loading}
              />
            </div>

            <div className='form-group'>
              <label htmlFor='email'>Email Address</label>
              <input
                type='email'
                id='email'
                name='email'
                value={formData.email}
                onChange={handleChange}
                placeholder='Enter your email'
                disabled={loading}
              />
            </div>

            <div className='form-group'>
              <label htmlFor='password'>Password</label>
              <div className='password-input-wrapper'>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id='password'
                  name='password'
                  value={formData.password}
                  onChange={handleChange}
                  placeholder='Create a password'
                  disabled={loading}
                />
                <button
                  type='button'
                  className='password-toggle'
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            <div className='form-group'>
              <label htmlFor='confirmPassword'>Confirm Password</label>
              <div className='password-input-wrapper'>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id='confirmPassword'
                  name='confirmPassword'
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder='Confirm your password'
                  disabled={loading}
                />
                <button
                  type='button'
                  className='password-toggle'
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            <div className='form-checkbox'>
              <input
                type='checkbox'
                id='terms'
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                disabled={loading}
              />
              <label htmlFor='terms'>
                I agree to the{' '}
                <Link to='/terms' className='auth-link-inline'>
                  Terms and Conditions
                </Link>
              </label>
            </div>

            <button
              type='submit'
              className='auth-button'
              disabled={loading}
            >
              {loading ? <Spinner size='sm' color='white' /> : 'Create Account'}
            </button>
          </form>

          <div className='auth-footer'>
            <p>
              Already have an account?{' '}
              <Link to='/login' className='auth-link'>
                Log in here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
